/**
 * Impressum — österreichische Pflicht-Angaben nach §§ 24, 25 MedienG, § 5 ECG,
 * § 14 UGB. Inhalt 1:1 von www.vorlagenportal.at/impressum/ übernommen
 * (gleicher Betreiber). Photo-Credits + Bild-Sektionen wurden bewusst weggelassen,
 * da ET diese Visuals nicht verwendet.
 *
 * Erreichbar via Footer-Link auf der Landing-Page; öffentliche Route ohne
 * Auth-Gate.
 */
import React from "react";
import { Link } from "react-router-dom";
import Logo from "../components/Logo";
import { ArrowLeft } from "@phosphor-icons/react";


function Section({ heading, children, testid }) {
  return (
    <section className="mb-10" data-testid={testid}>
      <h2 className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-3">
        {heading}
      </h2>
      <div className="text-sm leading-relaxed text-ink-900 space-y-1.5">
        {children}
      </div>
    </section>
  );
}


export default function ImpressumPage() {
  return (
    <div className="min-h-screen bg-ink-50" data-testid="impressum-page">
      {/* Slim header (no full marketing nav — keep it minimal as requested) */}
      <header className="border-b border-ink-200 bg-white sticky top-0 z-10">
        <div className="mx-auto max-w-3xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2" aria-label="Zur Startseite">
            <Logo size="sm" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-warm-600 hover:text-ink-900 transition-crisp"
            data-testid="impressum-back-home"
          >
            <ArrowLeft size={13} weight="bold" />
            Zur Startseite
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-14">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
          Pflichtangaben
        </div>
        <h1 className="font-cabinet text-4xl sm:text-5xl tracking-tight text-ink-900 font-medium leading-none mb-3">
          Kontakt & Impressum
        </h1>
        <p className="text-base text-warm-700 mb-12 max-w-2xl leading-relaxed">
          Pflichtangaben gemäß § 5 ECG, § 14 UGB und §§ 24 ff MedienG für die Anwendung
          „ET — Das Einstufungs-Tool" sowie den Webauftritt unter dieser Domain.
        </p>

        <Section heading="Medieninhaber & Betreiber" testid="impressum-betreiber">
          <p className="font-medium text-ink-900">
            Kraft &amp; Kronberger Fachpublikationen GmbH
          </p>
          <p>Seepark 4</p>
          <p>A-7222 Rohrbach bei Mattersburg</p>
          <p className="pt-2">
            <a
              href="mailto:office@vorlagenportal.at"
              className="text-brand-600 underline underline-offset-2 hover:text-brand-700"
              data-testid="impressum-email"
            >
              office@vorlagenportal.at
            </a>
          </p>
        </Section>

        <Section heading="Kontaktaufnahme" testid="impressum-kontakt">
          <p>
            Sie möchten uns kontaktieren? Bitte senden Sie uns ein E-Mail an{" "}
            <a
              href="mailto:office@vorlagenportal.at"
              className="text-brand-600 underline underline-offset-2 hover:text-brand-700"
            >
              office@vorlagenportal.at
            </a>
            .
          </p>
          <p className="pt-2 text-warm-600 text-[13px]">
            Bitte beachten Sie, dass wir kein Beratungsdienst sind und keine
            Fachanfragen annehmen können.
          </p>
        </Section>

        <Section heading="Unternehmensdaten" testid="impressum-firma">
          <p>
            <strong className="text-warm-700 font-semibold">Geschäftsführer:</strong>{" "}
            Mag. Rainer Kraft, Birgit Kronberger, MBA
          </p>
          <p>
            <strong className="text-warm-700 font-semibold">Firmenbuch:</strong>{" "}
            FN 473571 d
          </p>
          <p>
            <strong className="text-warm-700 font-semibold">Firmenbuchgericht:</strong>{" "}
            LG Eisenstadt
          </p>
          <p>
            <strong className="text-warm-700 font-semibold">UID-Nummer:</strong>{" "}
            ATU72406826
          </p>
          <p>
            <strong className="text-warm-700 font-semibold">Aufsichtsbehörde:</strong>{" "}
            Bezirkshauptmannschaft Mattersburg
          </p>
          <p>
            <strong className="text-warm-700 font-semibold">Gerichtsstand:</strong>{" "}
            Mattersburg
          </p>
        </Section>

        <Section heading="Rechtliche Dokumente" testid="impressum-rechtsdokumente">
          <ul className="space-y-1 list-disc list-inside marker:text-warm-400">
            <li>
              <a
                href="https://vorlagenportal.at/agb/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 underline underline-offset-2 hover:text-brand-700"
              >
                Allgemeine Geschäftsbedingungen
              </a>
            </li>
            <li>
              <a
                href="https://vorlagenportal.at/haftungsausschluss/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 underline underline-offset-2 hover:text-brand-700"
              >
                Haftungsausschluss
              </a>
            </li>
            <li>
              <a
                href="https://vorlagenportal.at/datenschutzbestimmungen/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-600 underline underline-offset-2 hover:text-brand-700"
              >
                Datenschutzbestimmungen
              </a>
            </li>
          </ul>
        </Section>

        <Section heading="Kammerzugehörigkeit" testid="impressum-kammer">
          <p>
            Mitglied der Wirtschaftskammer Österreich, Sparte Information und Consulting —
            Fachverband Unternehmensberatung und Informationstechnologie. Gewerbe:
            Dienstleistungen in der automatischen Datenverarbeitung und Informationstechnik.
          </p>
          <p>
            Mitglied der Wirtschaftskammer Österreich, Sparte Tourismus und
            Freizeitwirtschaft — Fachgruppe Freizeit- und Sportbetriebe, Berufszweig:
            Organisation und Vermittlung von Veranstaltungen.
          </p>
        </Section>

        <Section heading="Anwendbare Rechtsvorschriften" testid="impressum-vorschriften">
          <p>Es gelten folgende Rechtsvorschriften:</p>
          <p>
            <a
              href="https://www.ris.bka.gv.at/GeltendeFassung.wxe?Abfrage=Bundesnormen&Gesetzesnummer=10007517"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 underline underline-offset-2 hover:text-brand-700"
            >
              Gewerbeordnung 1994
            </a>
          </p>
        </Section>
      </main>

      {/* Slim footer */}
      <footer className="border-t border-ink-200 bg-white">
        <div className="mx-auto max-w-3xl px-6 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="text-[11px] text-warm-500">
            © {new Date().getFullYear()} ET · Das Einstufungs-Tool by Vorlagenportal
          </div>
          <Link
            to="/"
            className="text-[11px] text-warm-500 hover:text-ink-900 transition-crisp"
          >
            Zur Startseite
          </Link>
        </div>
      </footer>
    </div>
  );
}
